import { AxiosRequestConfig } from "axios";
import apiClient from "./apiClient";

/**
 * TanStack Query fetchers built on the shared axios client.
 *
 * Because they go through `apiClient`, they inherit every cross-cutting concern for free —
 * `withCredentials` cookie auth, the `application-name` header, the 401 → redirect, and the global
 * error dialog — with no duplication. Query functions return the response BODY (what a `useQuery`
 * consumer wants), not the axios envelope.
 */

/** GET `url` and return the response body. */
export async function apiQueryFn<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.get<T>(url, config);
  return res.data;
}

/** The shape every server-paged list endpoint returns (see MtcList). */
export interface ListApiResponse<T> {
  data: T[];
  count: number;
}

/** GET a server-paged list endpoint and return its `{ data, count }` body. */
export async function listQueryFn<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ListApiResponse<T>> {
  const res = await apiClient.get<ListApiResponse<T>>(url, config);
  return res.data;
}

/**
 * POST a server-paged list SEARCH and return its `{ data, count }` body.
 *
 * A POST that reads, deliberately: it exists so that search terms which are patient demographics
 * travel in a request body rather than in a URL, where the ALB, CloudFront, browser history and
 * every intermediary would log them outside the audited PHI perimeter (AUD-21). It is otherwise the
 * same request as `listQueryFn` — same axios client, so the same cookie auth, `application-name`
 * header, 401 handling and error dialog — and the same `{ data, count }` contract.
 */
export async function listSearchQueryFn<T>(
  url: string,
  body: unknown,
  config?: AxiosRequestConfig,
): Promise<ListApiResponse<T>> {
  const res = await apiClient.post<ListApiResponse<T>>(url, body, config);
  return res.data;
}
